import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Leaf, Calendar, Clock, MapPin, Loader2, Check, User, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
const FR_DAYS = ["dimanche","lundi","mardi","mercredi","jeudi","vendredi","samedi"];
const FR_MONTHS = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];

function formatDateFr(iso) {
  const d = new Date(iso);
  return `${FR_DAYS[d.getDay()]} ${d.getDate()} ${FR_MONTHS[d.getMonth()]}`;
}
function formatTimeFr(iso) {
  const d = new Date(iso);
  return d.toTimeString().slice(0, 5).replace(":", "h");
}
function groupByDay(slots) {
  const g = {};
  slots.forEach((s) => {
    const key = new Date(s).toISOString().slice(0, 10);
    (g[key] = g[key] || []).push(s);
  });
  return g;
}

export default function PublicBooking() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");
  const [form, setForm] = useState({ first: "", last: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/booking/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Praticien introuvable"))
      .finally(() => setLoading(false));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return toast.error("Choisissez d'abord un créneau");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/booking/${slug}`, {
        starts_at: selectedSlot,
        patient_first_name: form.first,
        patient_last_name: form.last,
        patient_email: form.email,
        patient_phone: form.phone,
        notes: form.notes,
      });
      setConfirmed(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la réservation");
    }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}>
      <Loader2 className="w-8 h-8 text-sage animate-spin" />
    </div>
  );
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg)" }}>
      <div className="card-soft p-8 text-center max-w-md">
        <div className="text-6xl mb-4">🌿</div>
        <div className="font-display text-xl text-sage mb-2">{error}</div>
        <div className="text-sm text-muted-soft">Vérifiez le lien ou contactez votre praticien.</div>
      </div>
    </div>
  );

  if (confirmed) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg)" }} data-testid="booking-confirmed">
      <div className="card-soft p-8 text-center max-w-md">
        <div className="w-16 h-16 mx-auto rounded-full bg-sage-light flex items-center justify-center mb-4">
          <Check className="w-8 h-8 text-sage" />
        </div>
        <div className="font-display text-2xl text-sage mb-2">Rendez-vous confirmé 🌿</div>
        <div className="text-sm text-muted-soft mb-4">
          {formatDateFr(confirmed.confirmed_at)} à {formatTimeFr(confirmed.confirmed_at)}
        </div>
        <div className="text-xs text-muted-soft">
          Un email de confirmation vient de vous être envoyé.<br/>Un rappel arrivera la veille.
        </div>
      </div>
    </div>
  );

  const p = data.practitioner;
  const grouped = groupByDay(data.available_slots || []);
  const days = Object.keys(grouped).sort();

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="public-booking-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="card-soft p-6 sm:p-8 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-sage flex items-center justify-center flex-shrink-0">
              <Leaf className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-2xl">{p.full_name}</div>
              <div className="text-sm text-sage">{p.profession}</div>
              {p.adeli_number && <div className="text-xs text-muted-soft mt-1">N° ADELI/RPPS : {p.adeli_number}</div>}
              {p.address && <div className="text-xs text-muted-soft mt-1 flex items-center gap-1"><MapPin className="w-3 h-3"/>{p.address}</div>}
              <div className="text-xs text-muted-soft mt-2">
                💶 Séance {data.slot_minutes} min · {p.default_session_price} €
              </div>
            </div>
          </div>
        </div>

        <div className="card-soft p-6 sm:p-8 mb-6">
          <div className="font-display text-xl mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-sage" /> Choisissez un créneau
          </div>
          {days.length === 0 && (
            <div className="text-sm text-muted-soft py-8 text-center">
              Aucun créneau disponible dans les 14 prochains jours.<br/>
              Contactez directement le praticien pour trouver un moment.
            </div>
          )}
          <div className="space-y-4">
            {days.map((day) => (
              <div key={day}>
                <div className="text-xs uppercase tracking-wider text-sage font-medium mb-2">
                  {formatDateFr(day + "T12:00:00Z")}
                </div>
                <div className="flex flex-wrap gap-2">
                  {grouped[day].map((s) => (
                    <button
                      key={s}
                      onClick={() => setSelectedSlot(s)}
                      data-testid={`booking-slot-${s}`}
                      className={`px-3 py-2 rounded-xl text-sm border transition ${
                        selectedSlot === s
                          ? "bg-sage text-white border-sage"
                          : "bg-white border-border hover:bg-sage-light"
                      }`}
                    >
                      <Clock className="w-3 h-3 inline mr-1" />{formatTimeFr(s)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedSlot && (
          <form onSubmit={submit} className="card-soft p-6 sm:p-8" data-testid="booking-form">
            <div className="font-display text-xl mb-4">Vos coordonnées</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-muted-soft mb-1 block">Prénom *</span>
                <input className="input-soft" required value={form.first}
                  onChange={(e) => setForm({...form, first: e.target.value})}
                  data-testid="booking-first-name"/>
              </label>
              <label className="block">
                <span className="text-xs text-muted-soft mb-1 block">Nom *</span>
                <input className="input-soft" required value={form.last}
                  onChange={(e) => setForm({...form, last: e.target.value})}
                  data-testid="booking-last-name"/>
              </label>
            </div>
            <label className="block mt-3">
              <span className="text-xs text-muted-soft mb-1 block">Email *</span>
              <input type="email" className="input-soft" required value={form.email}
                onChange={(e) => setForm({...form, email: e.target.value})}
                data-testid="booking-email"/>
            </label>
            <label className="block mt-3">
              <span className="text-xs text-muted-soft mb-1 block">Téléphone *</span>
              <input className="input-soft" required value={form.phone}
                onChange={(e) => setForm({...form, phone: e.target.value})}
                data-testid="booking-phone"/>
            </label>
            <label className="block mt-3">
              <span className="text-xs text-muted-soft mb-1 block">Un mot pour le praticien (optionnel)</span>
              <textarea className="input-soft" rows={2} value={form.notes}
                onChange={(e) => setForm({...form, notes: e.target.value})}
                data-testid="booking-notes"/>
            </label>
            <button type="submit" disabled={submitting}
              className="btn-primary mt-5 w-full" data-testid="booking-submit">
              {submitting ? "Réservation…" : `Confirmer le ${formatDateFr(selectedSlot)} à ${formatTimeFr(selectedSlot)}`}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
